import { l as DocContext, k as jsxRuntimeExports, t as textRendererStyle, n as DynamicMath } from "./doenetml-drNYjJiF.js";
import React__default, { useContext, useRef } from "react";
import { j as jxg, h as syncLineFamilyVisibility, s as syncLayer, e as syncLineStrokeStyle, a as syncWithLabelToggle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, i as applyLineFamilyLabelPlacement, k as buildLineFamilyLabelAttributes, d as attachLabelHoverHighlight, c as computeLabelMaskCssStyle, l as stabilizeInitialLineFamilyLabelPlacement, r as removeJXGEventHandlers } from "./JSXGraphRenderer-BcR36ozd.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { BoardContext, LINE_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { ChoiceInputInlineContext } from "./choiceInput-QYG5RDnq.js";
import { a as buildLineLikeAttributes } from "./buildGraphicalAttributes-C7PtyKL7.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as resolveLineColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-tcr76K44.js";
const line = React__default.memo(function Line(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  Line.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  let lineJXG = useRef(null);
  const dragState = usePointerDragState();
  let previousWithLabel = useRef(null);
  let cancelInitialLabelPlacement = useRef(null);
  let pointCoords = useRef(null);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  const {
    lastPositionFromCore: lastPositionsFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalPoints);
  let switchable = useRef(false);
  switchable.current = SVs.switchable && !SVs.fixed;
  const { darkMode } = useContext(DocContext) || {};
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: lineJXG,
    destroy: () => deleteLineJXG(),
    cancelLabelPlacementRef: cancelInitialLabelPlacement
  });
  function createLineJXG() {
    if (board === null) {
      return null;
    }
    if (SVs.numericalPoints?.length !== 2 || SVs.numericalPoints.some((x) => x.length !== 2)) {
      lineJXG.current = null;
      return;
    }
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    var jsxLineAttributes = buildLineLikeAttributes({
      SVs,
      layerOffset: LINE_LAYER_OFFSET,
      fixed: fixed.current,
      fixLocation: fixLocation.current,
      darkMode,
      dashed: SVs.dashed
    });
    jsxLineAttributes.label = buildLineFamilyLabelAttributes({
      labelForGraph: SVs.labelForGraph,
      labelPosition: SVs.labelPosition,
      labelHasLatex: SVs.labelHasLatex,
      applyStyleToLabel: SVs.applyStyleToLabel,
      lineColor,
      layer: SVs.layer,
      maskLabel: SVs.maskLabel
    });
    let through = [
      [...SVs.numericalPoints[0]],
      [...SVs.numericalPoints[1]]
    ];
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    let newLineJXG = board.create(
      "line",
      through,
      jsxLineAttributes
    );
    newLineJXG.isDraggable = !fixLocation.current;
    function buildMoveArgs() {
      return {
        point1coords: pointCoords.current?.[0],
        point2coords: pointCoords.current?.[1]
      };
    }
    attachLineFamilyDragHandlers({
      jxg: newLineJXG,
      tag: 0,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      actions: {
        move: actions.moveLine,
        focus: actions.lineFocused,
        click: actions.lineClicked,
        clickPrelude: actions.switchLine
      },
      clickPreludeGate: switchable,
      snapshot: () => [
        [...newLineJXG.point1.coords.scrCoords],
        [...newLineJXG.point2.coords.scrCoords]
      ],
      buildTransientMoveArgs: (e, snap) => {
        const next = [];
        if (e.type === "pointermove" && dragState.pointerAtDown.current && snap) {
          for (let i = 0; i < 2; i++) {
            next.push(
              pointerEventToUserCoords(
                e,
                dragState.pointerAtDown.current,
                snap[i],
                board
              )
            );
          }
        } else {
          next.push([newLineJXG.point1.X(), newLineJXG.point1.Y()]);
          next.push([newLineJXG.point2.X(), newLineJXG.point2.Y()]);
        }
        pointCoords.current = next;
        return {
          ...buildMoveArgs(),
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: () => buildMoveArgs(),
      onDragApplied: () => {
        newLineJXG.point1.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastPositionsFromCore.current[0]
        );
        newLineJXG.point2.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastPositionsFromCore.current[1]
        );
      }
    });
    lineJXG.current = newLineJXG;
    attachLabelHoverHighlight({
      hoverTargetJXG: newLineJXG,
      getLabelJXG: () => lineJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    if (SVs.labelForGraph !== "" && newLineJXG.hasLabel) {
      cancelInitialLabelPlacement.current = stabilizeInitialLineFamilyLabelPlacement({
        board,
        lineLike: newLineJXG,
        applyPlacement: (forceFullUpdate) => {
          if (lineJXG.current !== newLineJXG || !newLineJXG.hasLabel) {
            return false;
          }
          applyLineFamilyLabelPlacement({
            board,
            lineLike: newLineJXG,
            labelPosition: SVs.labelPosition,
            forceFullUpdate
          });
          return true;
        }
      });
    }
    previousWithLabel.current = SVs.labelForGraph !== "";
  }
  function deleteLineJXG() {
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    if (!lineJXG.current) {
      return;
    }
    removeJXGEventHandlers(lineJXG.current);
    board?.removeObject(lineJXG.current);
    lineJXG.current = null;
  }
  if (board) {
    if (lineJXG.current === null) {
      createLineJXG();
    } else if (SVs.numericalPoints?.length !== 2 || SVs.numericalPoints.some((x) => x.length !== 2)) {
      deleteLineJXG();
    } else {
      let validCoords = true;
      for (let coords of [
        SVs.numericalPoints[0],
        SVs.numericalPoints[1]
      ]) {
        if (!Number.isFinite(coords[0])) {
          validCoords = false;
        }
        if (!Number.isFinite(coords[1])) {
          validCoords = false;
        }
      }
      lineJXG.current.point1.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalPoints[0]
      );
      lineJXG.current.point2.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalPoints[1]
      );
      let visible = !SVs.hidden;
      syncLineFamilyVisibility(lineJXG.current, visible, validCoords);
      lineJXG.current.visProp.fixed = fixed.current;
      lineJXG.current.visProp.highlight = !fixLocation.current;
      lineJXG.current.isDraggable = !fixLocation.current;
      syncLayer(lineJXG.current, SVs.layer, LINE_LAYER_OFFSET);
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      syncLineStrokeStyle(lineJXG.current, {
        lineColor,
        lineWidth: SVs.selectedStyle.lineWidth,
        lineOpacity: SVs.selectedStyle.lineOpacity,
        dash: styleToDash(SVs.selectedStyle.lineStyle, SVs.dashed)
      });
      lineJXG.current.name = SVs.labelForGraph;
      syncWithLabelToggle(
        lineJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      lineJXG.current.needsUpdate = true;
      lineJXG.current.update();
      if (lineJXG.current.hasLabel && lineJXG.current.label) {
        const label = lineJXG.current.label;
        label.needsUpdate = true;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, lineColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        applyLineFamilyLabelPlacement({
          board,
          lineLike: lineJXG.current,
          labelPosition: SVs.labelPosition
        });
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
  }
  if (SVs.hidden) {
    return null;
  }
  const mathJaxify = "\\(" + SVs.latex + "\\)";
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: mathJaxify }) });
});
export {
  line as default
};
